import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface NumeroComiteResponse {
  numero: number;
  fechainicio?: string | Date;
  fechaVigencia?: string | Date;
  estado?: number;
  fechaCreacion?: string | Date;
  fechaModificacion?: string | Date;
}

export interface NumeroComiteRegistro {
  numero: number;
  fechainicio?: string | Date;
  estado?: number;
}

@Injectable({
  providedIn: 'root'
})
export class NumeroComiteService {
  private apiUrl = `${environment.apiUrl}/api/numeroComite`;

  constructor(private http: HttpClient) {
  }

  /**
   * Obtener todos los números de comité
   */
  getAll(): Observable<NumeroComiteResponse[]> {
    const url = `${this.apiUrl}`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((res) => {
        const datos = Array.isArray(res) ? res : (res?.data ?? []);
        return datos;
      })
    );
  }

  /**
   * Obtener todos los números de comité activos
   */
  getAllActivos(): Observable<NumeroComiteResponse[]> {
    const url = `${this.apiUrl}/activos`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((res) => {
        const datos = Array.isArray(res) ? res : (res?.data ?? []);
        return datos;
      })
    );
  }

  /**
   * Obtener integrantes activos del comité para formulario de reunión
   */
  getIntegrantesActivos(): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/integrantes-activo`, { withCredentials: true }).pipe(
      map((res) => {
        // La respuesta viene como { numeroComite: 3, data: [...] }
        if (res && res.data && Array.isArray(res.data)) {
          return res.data;
        }
        return Array.isArray(res) ? res : [];
      })
    );
  }

  getAllActivoscomite(): Observable<any[]> {
    const url = `${this.apiUrl}/integrantes-activo`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((res) => {
        let resultado: any[] = [];
        
        if (Array.isArray(res)) {
          resultado = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          resultado = res.data;
        } else {
          resultado = [];
        }
        
        return resultado;
      })
    );
  }


  /**
   * Obtener todos los números de comité inactivos
   */
  getAllInactivos(): Observable<NumeroComiteResponse[]> {
    const url = `${this.apiUrl}/inactivos`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((res) => {
        const datos = Array.isArray(res) ? res : (res?.data ?? []);
        return datos;
      })
    );
  }

  /**
   * Obtener el próximo número de comité disponible
   * Calcula basándose en el máximo número existente
   */
  getProximoNumero(): Observable<NumeroComiteResponse> {
    return this.http.get<any>(`${this.apiUrl}/siguiente`, { withCredentials: true }).pipe(
      map((response: any) => {
        // Manejar diferentes estructuras de respuesta
        let numero = response?.nextNumero || response?.data?.nextNumero || response?.numero;
        
        // Si es 0 o no existe, empezar desde 1
        if (!numero || numero === 0) {
          numero = 1;
        }
        
        return { numero, estado: 1 };
      })
    );
  }

  /**
   * Obtener número de comité por número
   */
  getByNumeroId(numero: number): Observable<NumeroComiteResponse> {
    return this.http.get<NumeroComiteResponse>(`${this.apiUrl}/${numero}`, { withCredentials: true });
  }

  /**
   * Obtener número de comité por número específico
   */
  getByNumero(numero: number): Observable<NumeroComiteResponse> {
    return this.getAll().pipe(
      map((numeros: NumeroComiteResponse[]) => {
        const encontrado = numeros.find(n => n.numero === numero);
        if (!encontrado) {
          throw new Error(`Número de comité ${numero} no encontrado`);
        }
        return encontrado;
      })
    );
  }

  /**
   * Crear nuevo registro de número de comité
   */
  create(numeroComite: NumeroComiteRegistro): Observable<NumeroComiteResponse> {
    const url = `${this.apiUrl}`;
    return this.http.post<NumeroComiteResponse>(url, numeroComite, { withCredentials: true });
  }

  /**
   * Actualizar número de comité existente
   */
  update(numero: number, numeroComite: NumeroComiteRegistro): Observable<NumeroComiteResponse> {
    const url = `${this.apiUrl}/${numero}`;
    return this.http.put<NumeroComiteResponse>(url, numeroComite,  { withCredentials: true });
  }

  /**
   * Inactivar número de comité y todos sus integrantes
   */
  inactivar(
  numero: number,
  payload?: {
    motivo: number;
    observaciones: string;
  }
): Observable<any> {
  const url = `${this.apiUrl}/inactivar/${numero}`;

  return this.http.put(
    url,
    payload ?? {},
    { withCredentials: true }
  );
}

  /**
   * Verificar si un número de comité ya existe
   */
  verificarExiste(numero: number): Observable<boolean> {
    // Validar contra activos para evitar duplicados
    return this.getAllActivos().pipe(
      map((numeros: NumeroComiteResponse[]) =>
        (numeros || []).some(n => Number(n.numero) === Number(numero))
      )
    );
  }

  /**
   * Obtener integrantes asociados a un número de comité
   * Endpoint: GET /api/numeroComite/{numero}
   * Retorna: { data: { NumeroComite, Comites: [...] } }
   */
  getIntegrantesPorNumero(numero: number): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/${numero}`).pipe(
      map((res) => {
        // Primero intentar extraer de la estructura anidada del backend
        if (res?.data?.Comites && Array.isArray(res.data.Comites)) {
          return res.data.Comites;
        }
        // Si es un array directo
        if (Array.isArray(res)) {
          return res;
        }
        // Si tiene Comites en raíz
        if (res?.Comites && Array.isArray(res.Comites)) {
          return res.Comites;
        }
        // Si es {data: [...]}
        if (res?.data && Array.isArray(res.data)) {
          return res.data;
        }
        return [];
      })
    );
  }
}

